import React, { useState, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import emailjs from '@emailjs/browser';
import toast from 'react-hot-toast';
import Section from '../components/ui/Section';
import Button from '../components/ui/Button';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import * as FaIcons from 'react-icons/fa';
import { cn } from '../lib/utils';

const Contact = () => {
  const formRef = useRef();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    subject: "I'd like to learn more about AGIR",
    message: ''
  });
  const [errors, setErrors] = useState({});

  const validate = () => {
    const newErrors = {};
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!formData.firstName.trim()) newErrors.firstName = 'First name is required';
    if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required';
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email address is required';
    } else if (!emailRegex.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.message.trim()) newErrors.message = 'Message cannot be empty';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear error when user types
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validate()) {
      toast.error("Please correct the errors in the form.");
      return;
    }

    setIsSubmitting(true);

    // NOTE: To make this fully functional in production, you must:
    // 1. Sign up at https://www.emailjs.com/
    // 2. Create an Email Service (e.g., Gmail)
    // 3. Create an Email Template
    // 4. Add your Service ID, Template ID, and Public Key below or in .env file
    
    const serviceID = import.meta.env.VITE_EMAILJS_SERVICE_ID || 'YOUR_SERVICE_ID';
    const templateID = import.meta.env.VITE_EMAILJS_TEMPLATE_ID || 'YOUR_TEMPLATE_ID';
    const publicKey = import.meta.env.VITE_EMAILJS_PUBLIC_KEY || 'YOUR_PUBLIC_KEY';

    try {
      // We attempt to send the form using EmailJS
      // If keys are placeholders, this will rightfully fail, triggering the error toast as requested.
      // For demo purposes, if you want to see the success state without keys, you would need to mock this call.
      
      await emailjs.sendForm(serviceID, templateID, formRef.current, publicKey);
      
      toast.success("Your message has been sent successfully.");
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        subject: "I'd like to learn more about AGIR",
        message: ''
      });
    } catch (error) {
      console.error('EmailJS Error:', error);
      
      // If the error is due to missing configuration (common in dev), log it clearly
      if (serviceID === 'YOUR_SERVICE_ID') {
        console.warn('EMAILJS CONFIG MISSING: Please set VITE_EMAILJS_SERVICE_ID, VITE_EMAILJS_TEMPLATE_ID, and VITE_EMAILJS_PUBLIC_KEY in your .env file.');
        // OPTIONAL: For pure demo purposes if keys are missing, we could simulate success:
        // toast.success("Your message has been sent successfully. (Simulation Mode)");
        // setFormData({ firstName: '', lastName: '', email: '', subject: "I'd like to learn more about AGIR", message: '' });
        // return;
      }

      toast.error("We’re sorry, your message could not be sent at this time. Please try again later.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Contact Us | AGIR USA</title>
        <meta name="description" content="Get in touch with AGIR USA. Whether you want to volunteer, donate, or just say hello, we'd love to hear from you." />
      </Helmet>

      {/* Warm Header with Background Image */}
      <div className="relative py-24 md:py-32 flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://media-manager-c.questera.ai/greta-media/b23737eec191c3f5b765303f85ce3a4932597ae054cc2b3dadfd019ed91c748ca1e6808982e16c775001fce05a7fb0b4/images/aW1hZ2UvanBlZw==/c399b3412a8dc01532054672dbc60616.jpeg" 
            alt="Community members sharing a moment of joy" 
            className="w-full h-full object-cover" 
          />
          <div className="absolute inset-0 bg-primary-dark/80 mix-blend-multiply"></div>
        </div>
        <div className="container mx-auto px-4 relative z-10 text-center text-white">
          <h1 className="font-heading text-4xl md:text-6xl font-bold mb-6">Let's Connect &<br/>Change Lives Together</h1>
          <p className="text-xl text-gray-200 max-w-2xl mx-auto leading-relaxed">
            Your voice matters to us. Whether you’re looking to partner, volunteer, or simply learn more about how God is moving in Guinea-Bissau, we are here with open hearts.
          </p>
        </div>
      </div>

      <Section>
        <div className="grid lg:grid-cols-2 gap-16">
          {/* Contact Form */}
          <div className="bg-white p-8 md:p-10 rounded-3xl shadow-xl border border-gray-100">
            <h2 className="font-heading text-2xl font-bold text-primary-dark mb-2">Send us a Message</h2>
            <p className="text-gray-500 mb-8 text-sm">We typically respond within 24-48 hours.</p>
            
            <form ref={formRef} onSubmit={handleSubmit} className="space-y-6" noValidate>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">
                    First Name <span className="text-red-500">*</span>
                  </label>
                  <input 
                    type="text" 
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    placeholder="Jane" 
                    className={cn(
                      "w-full p-4 bg-gray-50 border rounded-xl focus:ring-2 focus:ring-primary focus:bg-white outline-none transition-all",
                      errors.firstName ? "border-red-500 focus:ring-red-200" : "border-gray-200"
                    )}
                  />
                  {errors.firstName && <p className="text-red-500 text-xs mt-1 font-medium ml-1">{errors.firstName}</p>}
                </div>
                <div>
                  <label className="block text-sm font-bold text-gray-700 mb-2">
                    Last Name <span className="text-red-500">*</span>
                  </label>
                  <input 
                    type="text" 
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    placeholder="Doe" 
                    className={cn(
                      "w-full p-4 bg-gray-50 border rounded-xl focus:ring-2 focus:ring-primary focus:bg-white outline-none transition-all",
                      errors.lastName ? "border-red-500 focus:ring-red-200" : "border-gray-200"
                    )}
                  />
                  {errors.lastName && <p className="text-red-500 text-xs mt-1 font-medium ml-1">{errors.lastName}</p>}
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">
                  Email Address <span className="text-red-500">*</span>
                </label>
                <input 
                  type="email" 
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="jane@example.com" 
                  className={cn(
                    "w-full p-4 bg-gray-50 border rounded-xl focus:ring-2 focus:ring-primary focus:bg-white outline-none transition-all",
                    errors.email ? "border-red-500 focus:ring-red-200" : "border-gray-200"
                  )}
                />
                {errors.email && <p className="text-red-500 text-xs mt-1 font-medium ml-1">{errors.email}</p>}
              </div>
              
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">How can we help?</label>
                <select 
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  className="w-full p-4 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-primary focus:bg-white outline-none transition-all text-gray-600"
                >
                  <option>I'd like to learn more about AGIR</option>
                  <option>I'm interested in volunteering</option>
                  <option>Question about donations</option>
                  <option>Prayer request / Spiritual support</option>
                  <option>Other</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-2">
                  Your Message <span className="text-red-500">*</span>
                </label>
                <textarea 
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows="5" 
                  placeholder="Tell us more..." 
                  className={cn(
                    "w-full p-4 bg-gray-50 border rounded-xl focus:ring-2 focus:ring-primary focus:bg-white outline-none transition-all",
                    errors.message ? "border-red-500 focus:ring-red-200" : "border-gray-200"
                  )}
                ></textarea>
                {errors.message && <p className="text-red-500 text-xs mt-1 font-medium ml-1">{errors.message}</p>}
              </div>
              
              <Button 
                type="submit" 
                size="lg" 
                className="w-full py-4 text-lg"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <span className="flex items-center gap-2">
                    <SafeIcon icon={FiIcons.FiLoader} className="animate-spin" /> Sending...
                  </span>
                ) : "Send Message"}
              </Button>
            </form>
          </div>

          {/* Info & Relational Side */}
          <div className="space-y-10 py-4">
            <div>
              <h3 className="font-heading text-3xl font-bold text-primary-dark mb-6">We'd Love to Hear From You</h3>
              <p className="text-gray-600 text-lg leading-relaxed mb-8">
                You are not just a donor or a visitor; you are a partner in this mission. We value your prayers, your questions, and your support.
              </p>
              
              <div className="space-y-6">
                <div className="flex items-start gap-5">
                  <div className="w-12 h-12 bg-secondary/10 rounded-full flex items-center justify-center text-secondary text-xl shrink-0">
                    <SafeIcon icon={FiIcons.FiMail} />
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 text-lg">Email Us Directly</h4>
                    <p className="text-gray-500 text-sm mb-1">For general inquiries and partnerships</p>
                    <a href="mailto:Agir.usa.2025@gmail.com" className="text-primary font-bold hover:underline">Agir.usa.2025@gmail.com</a>
                  </div>
                </div>

                <div className="flex items-start gap-5">
                  <div className="w-12 h-12 bg-secondary/10 rounded-full flex items-center justify-center text-secondary text-xl shrink-0">
                    <SafeIcon icon={FiIcons.FiMapPin} />
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 text-lg">Guinea-Bissau Operations Center</h4>
                    <p className="text-gray-500 text-sm mb-1">Serving 1,000+ Children Daily</p>
                  </div>
                </div>

                <div className="flex items-start gap-5">
                  <div className="w-12 h-12 bg-secondary/10 rounded-full flex items-center justify-center text-secondary text-xl shrink-0">
                    <SafeIcon icon={FaIcons.FaPray} />
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 text-lg">Need Prayer?</h4>
                    <p className="text-gray-500 text-sm">
                      We believe in the power of prayer. If you have a request, select "Prayer request" in the form, and our team will pray for you.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Added Visual Element for Connection */}
            <div className="rounded-2xl overflow-hidden shadow-lg h-48 relative">
              <img 
                src="https://media-manager-c.questera.ai/greta-media/b23737eec191c3f5b765303f85ce3a4932597ae054cc2b3dadfd019ed91c748ca1e6808982e16c775001fce05a7fb0b4/images/aW1hZ2UvanBlZw==/5cb8114a405a2170c4ce4c44746d842d.jpeg" 
                alt="Community gathering" 
                className="w-full h-full object-cover" 
              />
              <div className="absolute inset-0 bg-gradient-to-r from-primary-dark/80 to-transparent flex items-center p-8">
                <p className="text-white font-heading font-bold text-xl italic max-w-xs">"Community is where humility and glory touch."</p>
              </div>
            </div>

            <div className="bg-primary-dark text-white p-8 rounded-2xl relative overflow-hidden">
              <div className="relative z-10">
                <SafeIcon icon={FaIcons.FaQuoteLeft} className="text-secondary/30 text-4xl mb-4" />
                <p className="text-xl font-heading font-medium italic mb-6">
                  "Let us not love with words or speech but with actions and in truth."
                </p>
                <div className="flex items-center gap-2 text-sm font-bold text-secondary uppercase tracking-widest">
                  — 1 John 3:18
                </div>
              </div>
            </div>
          </div>
        </div>
      </Section>
    </>
  );
};

export default Contact;